import React from 'react'
import styled from 'styled-components'
import AssessmentProgressBar from 'components/AssessmentProgressBar'
import CompletedAssessmentProgressBar from 'components/CompletedAssessmentProgressBar'

const ExpandableContentWrapper = styled.div`
  padding: 0;
  background-color: #fff;
  border-top: 1px solid #f0f0f0;
`

const ScheduleRow = styled.div`
  display: flex;
  height: 85px;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid #e8e8e8;
  width: 100%;
  background-color: ${props => {
    if (props.isDiagnosis) return '#F0EEFF';
    if (props.isCompleted) return '#FFFFFF';
    if (props.isOngoing) return '#FFFFFF';
    return '#FFFFFF';
  }};
`

const ScheduleInfo = styled.div`
  width: 25%;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-right: 10px;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 10px;
    bottom: 10px;
    width: 1px;
    background-color: #e8e8e8;
  }
`

const TitleStatusContainer = styled.div`
  display: flex;
  align-items: center;
`

const ScheduleTitle = styled.div`
  font-weight: ${props => props.isDiagnosis ? '700' : '700'};
  color: ${props => {
    if (props.isDiagnosis) return '#3249D7';

    return '#3249D7';
  }};
  font-size: 16px;
  line-height: 1.2;
  cursor: pointer;
`

const ScheduleStatus = styled.div`
  font-size: 16px;
  color: ${props => props.isCompleted ? '#3CB67E' : props.isOngoing ? '#000000' : props.isDiagnosis ? '#3249D7' : '#888888'};
  font-weight: 700;
  margin-right: 8px;
  margin-left: 15px;
  display: inline-block;
  
`

const ScheduleDate = styled.div`
font-family: Plus Jakarta Sans;
font-weight: 400;
font-size: 16px;
line-height: 20px;
letter-spacing: 0%;
color: #000000;
  margin-top: 4px;
`

const ProgressBarContainer = styled.div`
  width: 60%;
  padding: 0 16px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 10px;
    bottom: 10px;
    width: 1px;
    background-color: #e8e8e8;
  }
`


const ExpiredContainer = styled.div`
  width: 15%;
  text-align: center;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ExpiredCount = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-right: 5px;
`

const ExpiredLabel = styled.div`
  font-size: 12px;
  color: #666;
  font-weight: normal;
`

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Assessment start/end are stored as IST-wall-clock-as-UTC; render in UTC so the
  // date matches admin-react (else IST browsers show +1 day past 18:30 UTC).
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const ExpandableContent = ({ record, onAssessmentClick }) => {
  // Use assessmentDetails from the record if available
  const assessmentSchedules = record.assessmentDetails?.map((detail, index) => ({
    id: detail.auditId || `schedule-${index}-${record.scheduleId}`,
    title: detail.title || `Schedule ${index + 1}`,
    isOneTime: detail.isOneTime || false,
    status: detail.status || 'Upcoming',
    date:
      detail.startTime && detail.endTime
        ? `${formatDate(detail.startTime)} - ${formatDate(detail.endTime)}`
        : detail.endTime
          ? formatDate(detail.endTime)
          : '-',
    assessmentsTaken: detail.takenCount || 0,
    assessmentsSent: detail.assignedCount || 0,
    expired: detail.expiredCount || 0
  })) || []

  const getCleanScheduleName = (rec) => {
    const cName = rec.candidateListName;
    if (cName && cName !== 'N/A') {
      return cName.replace(/\s*-\s*Student List$/i, '').trim();
    }
    return rec.scheduleName || rec.name;
  }

  const diagnosisSchedule = {
    id: `diagnosis-${record.scheduleId}`,
    title: 'Diagnosis',
    status: record.diagnosisStatus || 'Upcoming',
    date: null,
    assessmentsTaken: record.totalDiagnosisTaken || record.diagnosisCompleted || 0,
    // Diagnosis was sent to the full roster (2 tests each), not just the active
    // candidates — use the full-roster count so the row matches the drill-in.
    assessmentsSent: ((record.totalListCandidates ?? record.totalCandidates) || 0) * 2,
    expired: null
  }

  // Only show diagnosis row for communication and aptitude types
  const normalizedType = (record.assessmentType || '').toLowerCase().replace(/[\s-]+/g, '_')
  const hasDiagnosis = ['communication', 'aptitude', 'hinglish'].includes(normalizedType)
  const schedules = record.isOneTime ? assessmentSchedules : (hasDiagnosis ? [diagnosisSchedule, ...assessmentSchedules] : assessmentSchedules)

  return (
    <ExpandableContentWrapper>
      {schedules.map((schedule, index) => {
        const isDiagnosis = schedule.title === 'Diagnosis';
        const isCompleted = schedule.status === 'Completed';
        const isOngoing = schedule.status === 'Ongoing';
        const isUpcoming = schedule.status === 'Upcoming';

        return (
          <ScheduleRow
            key={schedule.id}
            isDiagnosis={isDiagnosis}
            isOneTime={schedule.isOneTime}
            isCompleted={isCompleted}
            isOngoing={isOngoing}
          >
            <ScheduleInfo>
              <TitleStatusContainer>
                <ScheduleTitle
                  isDiagnosis={isDiagnosis}
                  completed={isCompleted}
                  upcoming={isUpcoming}
                  onClick={() => {
                    if (isUpcoming) return; // Disable click for upcoming schedules
                    if (isDiagnosis) {
                      // For Diagnosis, pass the schedule info with isDiagnosis flag
                      const diagnosisData = {
                        ...record,
                        scheduleId: record.scheduleId,
                        isDiagnosis: true,
                        assessmentType: record.assessmentType,
                        scheduleName: getCleanScheduleName(record),
                      }
                      console.log('[ExpandableContent] Diagnosis clicked, passing:', diagnosisData)
                      onAssessmentClick(diagnosisData)
                    } else {
                      const detail = record.assessmentDetails?.find(d =>
                        d.auditId === schedule.id ||
                        d.assessmentInstituteMapId === schedule.id // Fallback if ID came from mapId
                      ) || record.assessmentDetails?.[index - 1] // Fallback index (since diagnosis is 0)

                      // Pass the specific detail merged with parent info if needed, 
                      // or just the detail. The user wanted "assessment with having appropriate assessmentInstituteMapId"
                      // We'll construct an object that looks like what AssessmentDetails expects.
                      const assessmentToPass = {
                        ...record, // Keep parent info like name, type etc
                        ...detail, // Overwrite with specific instance info
                        id: detail?.assessmentInstituteMapId,
                        assessmentInstituteMapId: detail?.assessmentInstituteMapId,
                        scheduleName: getCleanScheduleName(record),
                        scheduleNumber: index,
                        // Ensure scheduleId is preserved if needed, or used from parent
                      }
                      onAssessmentClick(assessmentToPass)
                    }
                  }}
                  style={{
                    cursor: isUpcoming ? 'not-allowed' : 'pointer',
                    opacity: isUpcoming ? 0.5 : 1
                  }}
                >
                  {schedule.title}
                </ScheduleTitle>

                <ScheduleStatus
                  isDiagnosis={isDiagnosis}
                  isCompleted={isCompleted}
                  isOngoing={isOngoing}
                >
                  {schedule.status}
                </ScheduleStatus>
              </TitleStatusContainer>

              <ScheduleDate>
                {!isDiagnosis && schedule.date}
              </ScheduleDate>
            </ScheduleInfo>

            <ProgressBarContainer>
              {isCompleted && !isDiagnosis ? (
                <CompletedAssessmentProgressBar
                  sent={schedule.assessmentsSent}
                  completed={schedule.assessmentsTaken}
                />
              ) : (
                <AssessmentProgressBar
                  sent={schedule.assessmentsSent}
                  completed={schedule.assessmentsTaken}
                />
              )}
            </ProgressBarContainer>

            <ExpiredContainer>
              {!isDiagnosis && (
                <>
                  <ExpiredCount>
                    {schedule.expired === 'NA' ? 'NA' : schedule.expired}
                  </ExpiredCount>
                  {schedule.expired !== 'NA' && (
                    <ExpiredLabel>Expired</ExpiredLabel>
                  )}
                </>
              )}
            </ExpiredContainer>
          </ScheduleRow>
        );
      })}
    </ExpandableContentWrapper >
  )
}

export default ExpandableContent
